import { HttpParams } from '@angular/common/http';
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { FilterGroupComponent } from '@app/components/filter-group/filter-group.component';
import { ModalComponent } from '@app/components/info-modal/modal/modal.component';
import { DropDownButtonComponent } from '@app/components/input/drop-down-button/drop-down-button.component';
import { LoadingSpinnerComponent } from '@app/components/loading-spinner/loading-spinner.component';
import { LayerSelectorComponent } from '@app/components/map/layer-selector/layer-selector.component';
import { MapComponent } from '@app/components/map/map.component';
import { MultiLinkAccordionComponent } from '@app/components/multi-link-accordion/multi-link-accordion.component';
import { PageErrorComponent } from '@app/components/page-error/page-error.component';
import { DropDownButton } from '@app/models/elements/drop-down-button';
import { ExclusiveFilterConfig } from '@app/models/filters/exclusive-filter-config';
import { ClassAmmFilterMapper, FilterMapper } from '@app/models/filters/filter-mapper';
import { FilterStatus } from '@app/models/filters/filter-status';
import * as FilterType from '@app/models/filters/filter-type';
import { GenericFilterConfig } from '@app/models/filters/generic-filter-config';
import { DynamicLegendWmsLayer } from '@app/models/map/DynamicLegendWmsLayer';
import { WmsLayer } from '@app/models/map/WmsLayer';
import { PageConfig } from '@app/models/page-config';
import { ApiCallerService } from '@app/services/api-caller.service';
import { RouterService } from '@app/services/router.service';
import saveAs from 'file-saver';
import * as L from 'leaflet';
import * as hardcodedValues from './hardcoded';

@Component({
	selector: 'app-flussi-view',
	standalone: true,
	imports: [
		MultiLinkAccordionComponent,
		DropDownButtonComponent,
		LoadingSpinnerComponent,
		MapComponent,
		FilterGroupComponent,
		PageErrorComponent,
	],
	templateUrl: './flussi-view.component.html',
	styleUrl: './flussi-view.component.css',
})
export class FlussiViewComponent {
	contentLoaded: boolean = false;
	pageConfig: PageConfig = {} as PageConfig;
	filterMapper: FilterMapper = new ClassAmmFilterMapper();
	filtersStatus: { [filterId: string]: { selectedPrimaryValues: string[]; selectedSecondaryValues: string[] } } = {};
	visibleFilters: GenericFilterConfig[] = [];

	layersInfo = hardcodedValues.layers;
	layers: WmsLayer[] = [];

	downloadDataDropdown: DropDownButton = {
		text: 'DOWNLOAD DATI',
		droppedItems: [
			{
				text: 'VISUALIZZA',
				onClick: () => this.visualizeReport(),
				iconName: 'it-password-visible',
				highlight: true,
			},
			{
				text: 'SCARICA PDF',
				onClick: () => this.downloadPdfReport(),
				iconName: 'it-file-pdf-ext',
				highlight: false,
			},
			{
				text: 'SCARICA CSV',
				onClick: () => this.downloadCsvReport(),
				iconName: 'it-file-csv',
				highlight: false,
			},
			{
				text: 'SCARICA EXCEL',
				onClick: () => this.downloadXlsReport(),
				iconName: 'it-file-sheet',
				highlight: false,
			},
		],
	};

	downloadMapDropdown: DropDownButton = {
		text: 'DOWNLOAD MAPPA',
		droppedItems: [
			{
				text: 'SCARICA SHP',
				onClick: () => this.downloadMapShp(),
				iconName: 'it-file-compressed',
				highlight: false,
			},
		],
	};

	showDownloadDataLoadingSpinner = false;
	showDownloadMapLoadingSpinner = false;
	showReportError: boolean = false;
	showPageError: boolean = false;

	constructor(
		private apiCallerService: ApiCallerService,
		private router: Router,
		private routerService: RouterService,
	) {
		for (let li of this.layersInfo) {
			let layer = new DynamicLegendWmsLayer(
				li.name,
				li.visibleFiltersIds,
				li.modal,
				li.themes,
				li.selected,
				li.legend,
			);
			this.layers.push(layer);
		}
	}

	ngOnInit() {
		this.routerService.setActivePage(this.router.url);
		this.apiCallerService.getFlussiPageConfig(
			(data: PageConfig) => {
				this.pageConfig = data;
				this.initFilterStatus();
				this.setVisibleFilters(this.layers);
			}, // next
			() => {}, // error
			() => (this.contentLoaded = true), // complete
		);
	}

	/**
	 * Updates the filters status map with the new status received from the filter that changed.
	 * @param newStatus status of the changed filter
	 */
	setNewFilterStatus = (newStatus: FilterStatus) => {
		this.filtersStatus = {
			...this.filtersStatus,
			[newStatus.filterId]: {
				selectedPrimaryValues: newStatus.selectedPrimaryValues,
				selectedSecondaryValues: newStatus.selectedSecondaryValues,
			},
		};

		// console.log('FLUSSI.setNewFilterStatus: ', this.filtersStatus);
	};

	/**
	 * Sets visible filters.
	 */
	private setVisibleFilters = (layers: WmsLayer[]) => {
		let selectedLayerInfo = layers.find((l) => l.selected);
		let visibleFiltersIds = selectedLayerInfo?.visibleFilterIds;
		this.visibleFilters = [...this.pageConfig.filters.filter((f) => visibleFiltersIds?.includes(f.id))];
	};

	onMapLoad(map: L.Map) {
		return;
	}

	/**
	 * When in an exclusive filter a new filter is selected, this function allows to remove from the filtersStatus dictionary the values selected for the previous filter
	 * @param previouslySelectedId id of the previously selected filter in the exclusive filters
	 */
	removeDataOfPreviouslySelectedFilter = (previouslySelectedId: string) => {
		delete this.filtersStatus[previouslySelectedId];
	};

	/**
	 * Initializes filtersStatus with filters default values
	 */
	private initFilterStatus = () => {
		for (const filter of this.pageConfig.filters) {
			switch (filter.type) {
				case FilterType.EXCLUSIVE:
					// For an exclusive filter, we need to init the filterStatus with the default values of the filter visible by default
					// which is in the default values of the selector filter
					let casted = filter as ExclusiveFilterConfig;
					let initallyVisibleFilter = casted.availableFilters.find(
						(af) => af.id == casted.selector.defaultValues[0],
					)!;
					this.filtersStatus[initallyVisibleFilter.id] = {
						selectedPrimaryValues: [...initallyVisibleFilter.defaultValues],
						selectedSecondaryValues: [...initallyVisibleFilter.defaultValues],
					};
					break;
				default:
					this.filtersStatus[filter.id] = {
						selectedPrimaryValues: [...filter.defaultValues],
						selectedSecondaryValues: [...filter.defaultValues],
					};
					break;
			}
		}
	};

	visualizeReport = () => {
		let params = this.loadReportParams('pdf');
		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getFlussiReport(
			params,
			(data: Blob) => {
				this.showDownloadDataLoadingSpinner = false;
				this.openNewWindow(data);
			},
			() => {
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	};

	/**
	 * Prepares GET query params and sends GET request to /report. At response received, report download as PDF is started.
	 */
	downloadPdfReport = () => {
		this.showReportError = false;
		let params = this.loadReportParams('pdf');

		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getFlussiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadDataLoadingSpinner = false;
				saveAs(data, 'MTSReport.pdf');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	};

	downloadCsvReport() {
		this.showReportError = false;
		let params = this.loadReportParams('csv');

		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getFlussiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadDataLoadingSpinner = false;
				saveAs(data, 'MTSReport.zip');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	}

	downloadXlsReport() {
		this.showReportError = false;
		let params = this.loadReportParams('xls');

		this.showDownloadDataLoadingSpinner = true;
		this.apiCallerService.getFlussiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadDataLoadingSpinner = false;
				saveAs(data, 'MTSReport.xls');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	}

	downloadMapShp() {
		this.showReportError = false;
		let params = this.loadReportParams('shp');

		this.showDownloadMapLoadingSpinner = true;
		this.apiCallerService.getFlussiReport(
			params,
			(data: Blob) => {
				// next
				this.showDownloadMapLoadingSpinner = false;
				saveAs(data, 'MTSReport.zip');
			},
			() => {
				// error
				this.showDownloadDataLoadingSpinner = false;
				this.showReportError = true;
			},
		);
	}

	/**
	 * Opens new window and displays content of passed Blob
	 * @param data
	 */
	private openNewWindow = (data: Blob) => {
		const url = window.URL.createObjectURL(data);
		window.open(url, '_blank', 'height=600, width=1000');
	};

	/**
	 * Prepares params for report generation
	 * @param reportType
	 * @param reportFormat 'pdf' or 'xls'
	 */
	private loadReportParams = (reportFormat: string) => {
		let params = new HttpParams();

		for (const id in this.filtersStatus) {
			const values = this.filtersStatus[id].selectedPrimaryValues;

			for (const value of values) {
				params = params.append(id, value);
			}
		}

		params = params.append('reportFormat', reportFormat);

		return params;
	};
}
